import type { MouseEvent, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { topicLabel, voiceLabel } from '@gogrov/shared'
import { TopicGlyph } from '@/components/gg/TopicGlyph'
import { StoryPicture } from '@/components/story/StoryPicture'
import { ROUTES } from '@/components/gg/chrome'
import type { LibraryStory } from '@/types/library'

const DAY = 864e5
const d0 = (t: number) => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime() }

/** Today, Yesterday, or "Tuesday, Oct 21". */
export function dayLabel(t: number): string {
  const n = Math.round((d0(Date.now()) - d0(t)) / DAY)
  return n <= 0 ? 'Today' : n === 1 ? 'Yesterday' : new Date(t).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
}

/** Just now, 12 min ago, 3:40 PM, Yesterday, Oct 21. */
export function whenLabel(t: number): string {
  const m = Math.round((Date.now() - t) / 6e4)
  if (m < 1) return 'Just now'
  if (m < 60) return `${m} min ago`
  const n = Math.round((d0(Date.now()) - d0(t)) / DAY)
  if (n <= 0) return new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  if (n === 1) return 'Yesterday'
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export const fmtBytes = (n: number) => n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(2)} GB` : `${Math.max(0, Math.round(n / 1024 ** 2))} MB`
const andList = (a: string[]) => a.length <= 1 ? a.join('') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`

export interface RowAction { key: string; label: ReactNode; primary?: boolean; danger?: boolean; armed?: boolean; busy?: boolean; onClick: () => void }

/** A story in the Library (library.html v2.1). The whole card opens the story. */
export function LibraryRow({ s, actions }: { s: LibraryStory; actions: RowAction[] }) {
  const stop = (fn: () => void) => (e: MouseEvent) => { e.preventDefault(); e.stopPropagation(); fn() }
  const writing = s.stage === 'writing'
  const body = (
    <>
      <StoryPicture imageUrl={s.imageUrl} credit={s.imageCredit} topic={s.topic} ratio="1/1" radius={10} showCredit={false} className="libx__th" seedKey={s.id}
        story={{ id: s.story?.headline_id ?? s.id, topic: s.topic, headline: s.headline }} />
      <div className="libx__tp">
        <TopicGlyph topic={s.topic} size={22} />
        <span className="libx__lb">{topicLabel(s.topic)}</span>
        <span className="libx__when">{whenLabel(s.at)}</span>
      </div>
      <div className="libx__hl">{s.headline}</div>
      {writing ? (
        <>
          <div className="libx__pos">Emma is writing it</div>
          <div className="libx__bar" aria-hidden="true"><i /></div>
        </>
      ) : (
        <div className="libx__pos">Written as <b>{andList(s.positions.map((v) => voiceLabel(v)))}</b></div>
      )}
      <div className="libx__ft">
        {writing ? <span className="libx__chip">Writing</span>
          : s.postedAt ? <span className="libx__chip">Posted {whenLabel(s.postedAt).replace(/^Just now$/, 'just now')}</span>
          : s.recordedAt ? <span className="libx__chip">Recorded</span>
          : <span className="libx__chip">Built</span>}
        {s.toPhoneAt && <span className="libx__chip">Sent to your phone</span>}
        {s.savedAt && <span className="libx__chip">Saved to Photos</span>}
        {s.record && <span className="libx__chip">Public record</span>}
        {s.inNewsletter && <span className="libx__chip">In this week’s newsletter</span>}
        {s.takes > 0 && <span className="libx__sz">{s.takes} {s.takes === 1 ? 'take' : 'takes'} · {fmtBytes(s.takeBytes)}</span>}
      </div>
      {actions.length > 0 && (
        <div className="libx__acts">
          {actions.map((a) => (
            <button key={a.key} type="button" disabled={a.busy}
              className={`libx__ab${a.primary ? ' libx__ab--pri' : ''}${a.danger ? ' libx__ab--del' : ''}${a.armed ? ' is-armed' : ''}`}
              onClick={stop(a.onClick)}>{a.label}</button>
          ))}
        </div>
      )}
    </>
  )
  if (writing) return <div className="libx__st is-writing">{body}</div>
  return <Link className="libx__st" to={ROUTES.story(s.id)}>{body}</Link>
}
